import { type CSSProperties } from 'react'
import { cn } from '../lib/cn'

/**
 * ─────────────────────────────────────────────────────────────────
 *  PIXELLAB HERO ART SLOT  (design-system §8)
 * ─────────────────────────────────────────────────────────────────
 *  The hero illustration — "four adventurers relaxing by a fire" — is delivered
 *  by PixelLab and is NOT fabricated here (no binary art invented).
 *
 *  TO WIRE THE REAL ASSET:
 *    1. Drop the delivered file into engines/fireside/app/frontend/assets/
 *       (prefer SVG; else a 2× WebP/PNG with srcset). Pixel art must set
 *       `image-rendering: pixelated` and snap to a fixed display width.
 *    2. `import heroSrc from '../assets/<file>'` and pass `src={heroSrc}` below.
 *    3. Keep `aria-hidden`/empty alt — the art conveys NO information (§8.1).
 *
 *  Until then this renders a token-only firelight placeholder so the hero
 *  composes (mask + scrim + glow) with the art absent — the UI must read with
 *  the illustration removed (§8.5 / AX §7). The placeholder layers warm ember
 *  radials for depth, dusts the canvas with a faint ember-fleck texture (§5.1),
 *  and lets a few sparks rise so the fire feels alive — all motion is
 *  motion-safe (static under prefers-reduced-motion; see fireside.css).
 */
interface HeroArtProps {
  src?: string
  className?: string
}

/**
 * A few rising embers/sparks for the placeholder firelight. Positions cluster
 * around the fire (lower-center); size, timing and travel vary per spark so the
 * field feels organic. Negative delays pre-fill the field on first paint.
 * Shared styling + keyframes (and the reduced-motion gate) live in fireside.css
 * under `.fireside-ember` / `@keyframes fireside-ember-rise`.
 */
interface Ember {
  left: string
  bottom: string
  size: number // px
  duration: number // s
  delay: number // s — negative pre-fills the animation field
  rise: number // px — upward travel
  drift: number // px — lateral wander
}

const EMBERS: Ember[] = [
  { left: '44%', bottom: '38%', size: 6, duration: 8.5, delay: -1.2, rise: 240, drift: 16 },
  { left: '53%', bottom: '33%', size: 5, duration: 10.5, delay: -4.5, rise: 270, drift: -14 },
  { left: '49%', bottom: '42%', size: 7, duration: 9.5, delay: -6.5, rise: 210, drift: 10 },
  { left: '58%', bottom: '36%', size: 4, duration: 7.5, delay: -2.8, rise: 200, drift: -8 },
  { left: '40%', bottom: '30%', size: 5, duration: 11, delay: -8, rise: 280, drift: 20 },
  { left: '62%', bottom: '40%', size: 6, duration: 9, delay: -5.2, rise: 230, drift: -18 },
  { left: '47%', bottom: '28%', size: 4, duration: 8, delay: -3.6, rise: 250, drift: 6 },
]

export function HeroArt({ src, className }: HeroArtProps) {
  return (
    <div className={cn('pointer-events-none absolute inset-0 z-0 overflow-hidden', className)} aria-hidden="true">
      {src ? (
        <img
          src={src}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover"
          style={{ maskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)' }}
        />
      ) : (
        <>
          {/* Layered firelight placeholder (§8): a small, bright, low-opacity ember
              core over progressively broader, deeper, low-chroma warmth — depth and
              a sense of a real fire, never a flat fill. Full-chroma ember-400/500
              stays a low-opacity glow (§1.4); the large fills use low-chroma
              ember-700/900/950. Masked so the bottom dissolves into canvas (§8.2). */}
          <div
            data-fireside-hero-placeholder
            className="h-full w-full"
            style={{
              background:
                // hot ember core (ember-400 / --color-glow) — small + bright
                'radial-gradient(26% 22% at 50% 40%, oklch(82.8% 0.189 84.43 / 0.16), transparent 72%),' +
                // fire glow (ember-500)
                'radial-gradient(48% 40% at 50% 41%, oklch(76.9% 0.188 70.08 / 0.18), transparent 70%),' +
                // warm halo body (ember-700, low chroma)
                'radial-gradient(72% 56% at 50% 40%, oklch(55.5% 0.163 48.99 / 0.22), transparent 74%),' +
                // broad firelit room glow from the top (ember-900, low chroma)
                'radial-gradient(125% 90% at 50% 2%, oklch(41.4% 0.112 45.9 / 0.55), transparent 68%),' +
                // deep warm anchor rising from below for depth (ember-950)
                'radial-gradient(115% 75% at 50% 108%, oklch(27.9% 0.077 45.64 / 0.5), transparent 74%)',
              maskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
            }}
          />

          {/* Very-low-opacity warm ember-fleck texture on the canvas only (§5.1).
              Never on text — it lives inside the aria-hidden art behind the z-10 UI.
              Two offset, non-multiple tile sizes avoid an obvious grid. */}
          <div
            className="absolute inset-0"
            style={{
              opacity: 0.04,
              backgroundImage:
                'radial-gradient(oklch(87.9% 0.169 91.6 / 0.9) 0.5px, transparent 1.6px),' +
                'radial-gradient(oklch(76.9% 0.188 70.08 / 0.8) 0.5px, transparent 1.6px)',
              backgroundSize: '34px 34px, 53px 53px',
              backgroundPosition: '0 0, 17px 23px',
            }}
          />

          {/* Rising embers — motion-safe; static warm flecks for reduced motion
              (gated in fireside.css). Decorative only, behind the z-10 content. */}
          <div className="absolute inset-0">
            {EMBERS.map((ember) => (
              <span
                key={`${ember.left}-${ember.bottom}`}
                className="fireside-ember"
                style={
                  {
                    left: ember.left,
                    bottom: ember.bottom,
                    height: `${ember.size}px`,
                    width: `${ember.size}px`,
                    animationDuration: `${ember.duration}s`,
                    animationDelay: `${ember.delay}s`,
                    '--ember-rise': `${ember.rise}px`,
                    '--ember-drift': `${ember.drift}px`,
                  } as CSSProperties
                }
              />
            ))}
          </div>
        </>
      )}

      {/* Dissolve the scene into the canvas — no hard rectangle seam (§8.2). */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-canvas/40 to-canvas" />

      {/* Faint ember ambient at the hero center, tying the on-screen fire to the
          CTA's own warm glow (§8.4). */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(42% 36% at 50% 46%, oklch(76.9% 0.188 70.08 / 0.10), transparent 70%)',
        }}
      />
    </div>
  )
}
